// SignersPage — /sent-items/signers. Every pending signature, longest first.
//
// The Agreements page had two lenses over one registry: Threads and
// Submissions. Sent Items kept the first and, deliberately, did not keep the
// second — a row per DocuSeal submission is "the largest crime" the redesign
// exists to end. What went with it, though, was the only place to ask "who is
// sitting on a signature right now": per-signer status survives on each item's
// Full Details, so the question costs one page-open per item.
//
// So this is a REPORT, not a fourth view. The distinction is load-bearing:
//
//   • It is NOT in the view switcher. `all` / `waiting` / `recent` are three
//     orderings of the same canonical item list; this is a different unit of
//     row (a signature, not an item) and putting it in the switcher would say
//     otherwise. It is a quiet link in the Sent Items header.
//   • It carries no state, no signals and no actions. Every row links to the
//     item's Full Details, which is where all of that already lives — including
//     Remind, which is a per-document guard the server owns.
//   • It does not sort or filter what it renders. The server does both, exactly
//     as `SentItemList` is written not to (§6.1). What arrives is what is shown.
//
// Every control is URL-backed, so "who is WHP sitting on" is a link one
// operator sends another.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { formatWhen } from '@/lib/commandCenterClient';
import { OcEmpty, OcError, OcLoading } from '@/components/oc/bits';
import { describeWithheldKinds, describeWhere } from '@/lib/viewDocs';
import { fetchSentItemSigners } from '@/lib/sentItemsClient';
import type {
  SentItemSignerParty,
  SentItemSignerRow,
  SentItemSignersParams,
  SentItemSignersResponse,
} from '@/lib/sentItemsClient';

// ---------------------------------------------------------------------------
// URL ⇄ params
// ---------------------------------------------------------------------------

const PARTIES: readonly SentItemSignerParty[] = ['whp', 'recipient'];

function isParty(value: string | null): value is SentItemSignerParty {
  return value !== null && (PARTIES as readonly string[]).includes(value);
}

/** The report's filters, read off the URL. Unknown values are simply absent. */
export function signerParamsFromSearch(search: URLSearchParams): SentItemSignersParams {
  const out: SentItemSignersParams = {};
  const q = search.get('q');
  if (q && q.trim()) out.q = q.trim();
  const party = search.get('party');
  if (isParty(party)) out.party = party;
  return out;
}

/** Whose move it is, in the words the item rows already use. */
export const PARTY_LABEL: Record<SentItemSignerParty, string> = {
  whp: 'WHP',
  recipient: 'The person',
};

/**
 * "6 days" · "1 day" · "today".
 *
 * `daysWaiting` is whole days from the server, so a signature sent this morning
 * is 0 — and "0 days" reads like a bug. Said as "today" instead.
 */
export function waitingText(days: number): string {
  if (days <= 0) return 'today';
  return days === 1 ? '1 day' : `${days} days`;
}

/** "2 of 3" — where this signer sits in the document's roster. */
export function positionText(row: SentItemSignerRow): string | null {
  return row.position ? `${row.position.index} of ${row.position.total}` : null;
}

function loadError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Couldn't load who is signing (${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't load who is signing.";
}

// ---------------------------------------------------------------------------
// The table (pure — the page fetches, this renders)
// ---------------------------------------------------------------------------

export interface SignersTableProps {
  /** Rendered in the order given. This component never sorts. */
  rows: SentItemSignerRow[];
  emptyText: string;
}

export function SignersTable({ rows, emptyText }: SignersTableProps): React.JSX.Element {
  if (rows.length === 0) return <OcEmpty>{emptyText}</OcEmpty>;

  return (
    <div className="oc-table-wrap">
      <table className="oc-table">
        <thead>
          <tr>
            <th scope="col">Signer</th>
            <th scope="col">Document</th>
            <th scope="col">Whose move</th>
            <th scope="col">Waiting</th>
            <th scope="col">Sent</th>
            <th scope="col">Last reminder</th>
            <th scope="col">Last opened</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const where = describeWhere(r.propertyLabel, r.unitLabel);
            const position = positionText(r);
            return (
              <tr key={`${r.itemId}|${r.signer.role}|${r.signer.name}`}>
                <td>
                  {r.signer.name}
                  <span className="oc-cell-sub">
                    {r.signer.role}
                    {position ? ` · ${position}` : ''}
                  </span>
                </td>
                <td>
                  {/* The row's one door: Full Details, where the versions, the
                      signing spine and every action already are. */}
                  <Link to={r.href}>{r.kindLabel}</Link>
                  <span className="oc-cell-sub">{r.subject}</span>
                  {where && <span className="oc-cell-sub">{where}</span>}
                </td>
                <td>{PARTY_LABEL[r.signer.party]}</td>
                <td className="oc-cell-num" title={`Waiting since ${formatWhen(r.since)}`}>
                  {waitingText(r.daysWaiting)}
                </td>
                <td>{r.sentAt ? formatWhen(r.sentAt) : '—'}</td>
                <td>{r.lastReminderAt ? formatWhen(r.lastReminderAt) : '—'}</td>
                <td>{r.lastOpenedAt ? formatWhen(r.lastOpenedAt) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function SignersPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // Memoised on the WIRE QUERY rather than the URL object, so an unrelated
  // param change does not re-fetch the report underneath the operator.
  const wire = `${searchParams.get('q') ?? ''}|${searchParams.get('party') ?? ''}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => signerParamsFromSearch(searchParams), [wire]);

  const [data, setData] = useState<SentItemSignersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(0);

  // The search box is local so typing is not one request per keystroke; the URL
  // is written on submit, which is also what makes the view shareable.
  const [qInput, setQInput] = useState(params.q ?? '');
  useEffect(() => setQInput(params.q ?? ''), [params.q]);

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    const token = ++liveRef.current;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetchSentItemSigners(params);
        if (liveRef.current !== token) return;
        setData(res);
      } catch (err) {
        if (liveRef.current !== token) return;
        setError(loadError(err));
      } finally {
        if (liveRef.current === token) setLoading(false);
      }
    })();
  }, [params]);

  const rows = data?.rows ?? [];
  const withheldNote = describeWithheldKinds(data?.withheld ?? []);
  const party = params.party ?? null;

  const setParty = (next: SentItemSignerParty | null) =>
    updateParams((p) => {
      if (next) p.set('party', next);
      else p.delete('party');
    });

  return (
    <div className="oc-page">
      <header className="oc-page-header">
        <h1 className="oc-page-title">Signers</h1>
        <span className="oc-meta">
          Every signature still outstanding, longest wait first — one row per
          person, across everything in flight.
          {data?.generatedAt ? ` As of ${formatWhen(data.generatedAt)}.` : ''}
        </span>
        <Link className="btn" data-variant="ghost" data-size="sm" to="/sent-items">
          Back to Sent Items
        </Link>
      </header>

      <div className="oc-filterbar">
        <button
          type="button"
          className="oc-view-tab"
          data-active={party === null ? 'true' : undefined}
          onClick={() => setParty(null)}
        >
          Everyone
        </button>
        {PARTIES.map((p) => (
          <button
            key={p}
            type="button"
            className="oc-view-tab"
            data-active={party === p ? 'true' : undefined}
            onClick={() => setParty(p)}
          >
            {PARTY_LABEL[p]}
          </button>
        ))}
        <span className="oc-token" style={{ color: 'var(--fg-muted)' }}>
          {rows.length} waiting
        </span>
        <form
          className="oc-search"
          onSubmit={(e) => {
            e.preventDefault();
            updateParams((p) => {
              const v = qInput.trim();
              if (v) p.set('q', v);
              else p.delete('q');
            });
          }}
        >
          <input
            className="input"
            style={{ width: '100%' }}
            placeholder="Filter by signer, name, address…"
            aria-label="Filter signers"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
          />
        </form>
      </div>

      {/* A standing statement of scope, not a notification — the same posture
          Sent Items and the household docket take (C9). */}
      {withheldNote && <div className="oc-meta">{withheldNote}</div>}

      {error && <OcError>{error}</OcError>}

      {loading ? (
        <OcLoading label="Loading who is signing…" />
      ) : (
        <SignersTable
          rows={rows}
          emptyText={
            params.q || params.party
              ? 'Nobody matches those filters.'
              : 'Nothing is waiting on a signature.'
          }
        />
      )}
    </div>
  );
}
